import { useEffect, useMemo, useState } from "react";
import { annotBounds } from "../lib/annot-geometry.ts";
import type { PdfEngine } from "../lib/engine.ts";
import {
  isMarkup,
  quadBounds,
  type Annotation,
  type AnnotationStatus,
} from "../lib/protocol.ts";

const STATUS_META: Record<AnnotationStatus, { label: string; color: string }> = {
  accepted: { label: "Accepted", color: "#2f9e44" },
  rejected: { label: "Rejected", color: "#e03131" },
  completed: { label: "Completed", color: "#1971c2" },
  cancelled: { label: "Cancelled", color: "#868e96" },
};

/** Annotation kinds that carry comment metadata (everything except
 *  links and pending redactions). */
function commentMeta(a: Annotation): Exclude<Annotation, { kind: "link" | "redact" }> | null {
  return a.kind === "link" || a.kind === "redact" ? null : a;
}

export const COMMENT_META: Record<Annotation["kind"], { icon: string; label: string }> = {
  note: { icon: "ri-sticky-note-line", label: "Note" },
  text: { icon: "ri-text", label: "Text box" },
  highlight: { icon: "ri-mark-pen-line", label: "Highlight" },
  underline: { icon: "ri-underline", label: "Underline" },
  strikeout: { icon: "ri-strikethrough", label: "Strikeout" },
  squiggly: { icon: "ri-pulse-line", label: "Squiggly" },
  ink: { icon: "ri-pencil-line", label: "Drawing" },
  shape: { icon: "ri-shape-line", label: "Shape" },
  stamp: { icon: "ri-image-line", label: "Stamp" },
  redact: { icon: "ri-rectangle-fill", label: "Redaction" },
  link: { icon: "ri-link", label: "Link" },
};

/** One annotation's one-line description for rows and the summary. */
export function commentExcerpt(a: Annotation, markupText: Map<string, string>): string {
  if (a.kind === "note" || a.kind === "text") return a.value;
  // What the person wrote beats what the mark happens to cover.
  if (a.kind !== "link" && a.kind !== "redact" && a.contents?.trim()) return a.contents;
  if (isMarkup(a)) {
    const t = markupText.get(a.id);
    return t ? `“${t}”` : "";
  }
  if (a.kind === "shape") return a.shape;
  if (a.kind === "link") return a.url ?? `→ page ${(a.page ?? 0) + 1}`;
  return "";
}

/** Comments panel: every annotation grouped by page. Click a row to
 *  jump to (and select) it; notes and text boxes edit in place; each
 *  row deletes; the list copies as a plain-text summary. Markup rows
 *  show the text they cover (via the selection op, cached per id). */
export function CommentsPanel({
  engine,
  annots,
  author,
  onAuthorChange,
  onReply,
  onSetStatus,
  onSetContents,
  onJump,
  onEdit,
  onDelete,
  onCopySummary,
  onClose,
  paneWidth,
}: {
  engine: PdfEngine | null;
  annots: Annotation[];
  /** Comment author setting (persisted by the parent). */
  author: string;
  onAuthorChange: (v: string) => void;
  onReply: (a: Annotation, text: string) => void;
  onSetStatus: (a: Annotation, status: AnnotationStatus | null) => void;
  /** The annotation's own comment (/Contents). Only offered for kinds
   *  with no visible text of their own — a note's value IS its comment. */
  onSetContents: (a: Annotation, contents: string) => void;
  onJump: (a: Annotation) => void;
  onEdit: (a: Annotation) => void;
  onDelete: (id: string) => void;
  onCopySummary: (lines: string[]) => void;
  onClose: () => void;
  paneWidth: number;
}) {
  const [markupText, setMarkupText] = useState<Map<string, string>>(new Map());
  /** The row whose reply thread is expanded (one at a time). */
  const [openThread, setOpenThread] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const sorted = useMemo(
    () =>
      [...annots].sort(
        (a, b) => a.pageIndex - b.pageIndex || annotBounds(a)[1] - annotBounds(b)[1]
      ),
    [annots]
  );

  // Drain covered-text lookups for markup rows a few at a time; each
  // resolution re-runs the effect until nothing is missing. Failures
  // cache "" so they don't retry forever.
  useEffect(() => {
    if (!engine) return;
    const missing = sorted.filter((a) => isMarkup(a) && !markupText.has(a.id)).slice(0, 12);
    if (missing.length === 0) return;
    let dead = false;
    void (async () => {
      const entries: [string, string][] = [];
      for (const a of missing) {
        if (!isMarkup(a)) continue;
        try {
          const first = quadBounds(a.quads[0]!);
          const last = quadBounds(a.quads[a.quads.length - 1]!);
          const r = await engine.selection(
            a.pageIndex,
            [first.x + 1, first.y + first.height / 2],
            [last.x + last.width - 1, last.y + last.height / 2]
          );
          entries.push([a.id, r.text.replace(/\s+/g, " ").trim()]);
        } catch {
          entries.push([a.id, ""]);
        }
      }
      if (!dead) setMarkupText((m) => new Map([...m, ...entries]));
    })();
    return () => {
      dead = true;
    };
  }, [engine, sorted, markupText]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const summaryLines = () =>
    sorted.flatMap((a) => {
      const excerpt = commentExcerpt(a, markupText);
      const meta = commentMeta(a);
      const status = meta?.status ? ` [${STATUS_META[meta.status].label}]` : "";
      const who = meta?.author ? ` — ${meta.author}` : "";
      return [
        `Page ${a.pageIndex + 1} · ${COMMENT_META[a.kind].label}${status}${who}${excerpt ? `: ${excerpt}` : ""}`,
        ...(meta?.replies ?? []).map((r) => `    ↳ ${r.author ? `${r.author}: ` : ""}${r.text}`),
      ];
    });

  let lastPage = -1;
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col bg-[color:var(--color-bg)]" style={{ width: paneWidth }}>
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] p-3">
        <i className="ri-chat-3-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Comments</h2>
        <span className="text-xs text-[color:var(--color-fg-muted)]">{sorted.length}</span>
        <button
          type="button"
          title="Copy a plain-text summary of all comments"
          disabled={sorted.length === 0}
          onClick={() => onCopySummary(summaryLines())}
          className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
        >
          <i className="ri-file-copy-line" aria-hidden />
        </button>
        <button
          type="button"
          title="Close"
          aria-label="Close comments"
          onClick={onClose}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] px-3 py-1.5">
        <span className="text-[11px] text-[color:var(--color-fg-muted)]">Comment as</span>
        <input
          value={author}
          onChange={(e) => onAuthorChange(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Your name…"
          className="h-6 min-w-0 flex-1 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-xs"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {sorted.length === 0 && (
          <p className="p-3 text-xs text-[color:var(--color-fg-muted)]">
            No annotations yet — notes, highlights, shapes and drawings will be listed here.
          </p>
        )}
        {sorted.map((a) => {
          const meta = COMMENT_META[a.kind];
          const excerpt = commentExcerpt(a, markupText);
          const chip = "color" in a ? (a.color as string) : null;
          const threadMeta = commentMeta(a);
          const replyCount = threadMeta?.replies?.length ?? 0;
          const threadOpen = openThread === a.id;
          const header =
            a.pageIndex !== lastPage ? (
              <p className="mt-2 px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)] first:mt-0">
                Page {a.pageIndex + 1}
              </p>
            ) : null;
          lastPage = a.pageIndex;
          return (
            <div key={a.id}>
              {header}
              <div
                role="button"
                tabIndex={0}
                onClick={() => onJump(a)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onJump(a);
                }}
                className="group mb-1 cursor-pointer rounded-md border border-transparent px-2 py-1.5 hover:border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-subtle)]"
              >
                <div className="flex items-center gap-1.5">
                  <i
                    className={`${meta.icon} text-sm`}
                    style={chip ? { color: chip } : undefined}
                    aria-hidden
                  />
                  <span className="text-xs font-medium">{meta.label}</span>
                  {threadMeta?.author && (
                    <span className="min-w-0 truncate text-[10px] text-[color:var(--color-fg-muted)]">
                      {threadMeta.author}
                    </span>
                  )}
                  {threadMeta?.status && (
                    <span
                      className="shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase"
                      style={{
                        color: STATUS_META[threadMeta.status].color,
                        background: `${STATUS_META[threadMeta.status].color}22`,
                      }}
                    >
                      {STATUS_META[threadMeta.status].label}
                    </span>
                  )}
                  <span className="ml-auto hidden gap-0.5 group-hover:flex">
                    {(a.kind === "note" || a.kind === "text" || a.kind === "link") && (
                      <button
                        type="button"
                        title="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(a);
                        }}
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg)]"
                      >
                        <i className="ri-edit-line text-xs" aria-hidden />
                      </button>
                    )}
                    <button
                      type="button"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(a.id);
                      }}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg)]"
                    >
                      <i className="ri-delete-bin-line text-xs" aria-hidden />
                    </button>
                  </span>
                </div>
                {excerpt && (
                  <p className="mt-0.5 line-clamp-2 pl-5 text-[11px] leading-snug text-[color:var(--color-fg-muted)]">
                    {excerpt}
                  </p>
                )}
                {threadMeta && (
                  <div className="mt-0.5 pl-5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenThread(threadOpen ? null : a.id);
                        setReplyDraft("");
                      }}
                      className="text-[10px] text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
                    >
                      <i className="ri-chat-1-line" aria-hidden />{" "}
                      {replyCount > 0
                        ? `${replyCount} repl${replyCount === 1 ? "y" : "ies"}`
                        : "Reply"}
                      {threadOpen ? " ▴" : " ▾"}
                    </button>
                    {threadOpen && (
                      <div
                        className="mt-1 space-y-1 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1.5"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        {a.kind !== "note" &&
                          a.kind !== "text" &&
                          a.kind !== "redact" &&
                          a.kind !== "link" && (
                            <label className="block">
                              <span className="mb-0.5 block text-[10px] text-[color:var(--color-fg-muted)]">
                                Comment — also what a screen reader announces
                              </span>
                              <textarea
                                defaultValue={a.contents ?? ""}
                                rows={2}
                                onBlur={(e) => {
                                  const next = e.target.value.trim();
                                  if (next !== (a.contents ?? "").trim()) onSetContents(a, next);
                                }}
                                placeholder="Say what this mark means…"
                                className="w-full resize-y rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 py-1 text-[11px]"
                              />
                            </label>
                          )}
                        {(threadMeta.replies ?? []).map((r, ri) => (
                          <div key={ri} className="text-[11px] leading-snug">
                            <span className="font-medium">
                              {r.author?.trim() || "Anonymous"}
                            </span>
                            {r.date && (
                              <span className="ml-1 text-[9px] text-[color:var(--color-fg-muted)]">
                                {new Date(r.date).toLocaleString()}
                              </span>
                            )}
                            <div className="whitespace-pre-wrap text-[color:var(--color-fg-muted)]">
                              {r.text}
                            </div>
                          </div>
                        ))}
                        <input
                          value={replyDraft}
                          autoFocus
                          onChange={(e) => setReplyDraft(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter" && replyDraft.trim()) {
                              onReply(a, replyDraft);
                              setReplyDraft("");
                            }
                          }}
                          placeholder="Reply… (Enter to post)"
                          className="h-6 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-[11px]"
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-[color:var(--color-fg-muted)]">
                            Status
                          </span>
                          <select
                            value={threadMeta.status ?? ""}
                            onChange={(e) =>
                              onSetStatus(
                                a,
                                (e.target.value || null) as AnnotationStatus | null
                              )
                            }
                            className="h-6 min-w-0 flex-1 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1 text-[11px]"
                          >
                            <option value="">None</option>
                            <option value="accepted">Accepted</option>
                            <option value="rejected">Rejected</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
